import React from "react";
import ContractingSimplifiedDTO from "features/contracting/dtos/ContractingSimplifiedDTO";
import DateFormatter from "features/shared/components/formatters/DateFormatter";
import { useTranslation } from "react-i18next";
import RoundButton from "features/shared/components/buttons/RoundButton";
import _ from "lodash";
import { formatDate } from "features/shared/helpers/Formatters";
import CommodityIcon from "features/shared/components/icons/CommodityIcon";
import styles from "./contractingSimplified.module.scss";
import ContractingStateLabel from "./ContractingStateLabel";
import { getQueryParams } from "features/shared/helpers/queryParams";

type Props = {
  contracting: ContractingSimplifiedDTO;
  forAdminUse?: boolean;
};

const ContractingSimplified = ({ contracting, forAdminUse = false }: Props) => {
  // translations
  const tCustomerTypes = useTranslation("customer\\enums\\customerType").t;
  const tLabels = useTranslation("contracting\\contracting").t;

  // helpers
  const getCustomerInfo = () => {
    if (contracting.dateOfBirth) {
      return `${tLabels("contractingSimplified.dateOfBirth")} ${formatDate(
        contracting.dateOfBirth
      )}`;
    } else if (contracting.companyRegistrationNumber) {
      return `${tLabels("contractingSimplified.companyRegistrationNumber")} ${
        contracting.companyRegistrationNumber
      }`;
    }

    return "";
  };

  const getEffectiveDateClass = () => {
    return forAdminUse
      ? "d-none d-lg-block col"
      : "d-none d-sm-block col text-center text-lg-left";
  };

  // sub-renders
  const renderEffectiveDate = () => {
    if (contracting.contractExpectedEffectiveDate) {
      return (
        <>
          <span>{tLabels("contractingSimplified.from")} </span>
          <DateFormatter date={contracting.contractExpectedEffectiveDate} />
        </>
      );
    } else {
      return <>-</>;
    }
  };

  const renderCustomerCol = () => {
    if (forAdminUse) {
      return (
        <div className="d-none d-md-block col pr-3">
          <div className="text-truncate">{contracting.customerName}</div>
          <div className="small text-muted">{getCustomerInfo()}</div>
        </div>
      );
    }
  };

  const renderCustomerType = () => {
    return (
      <div className="row no-gutters">
        <div className="col-auto">
          <div className={styles.label}>
            <CommodityIcon commodity={contracting.commodity} includeColor />
          </div>
        </div>
        <div className="col">
          <div className="text-truncate">
            {tCustomerTypes(contracting.customerType!)}
          </div>
        </div>
      </div>
    );
  };

  const renderPlaceOfDelivery = () => {
    return (
      <div className="row no-gutters align-items-center">
        <div className="col-auto">
          <div className={`${styles.label} small text-muted`}>
            {tLabels("contractingSimplified.placeOfDelivery")}:
          </div>
        </div>
        <div className="col">{renderPlaceOfDeliveryIdentification()}</div>
      </div>
    );
  };

  const renderPlaceOfDeliveryIdentification = () => {
    if (contracting.placeOfDeliveryIdentification) {
      return (
        <div className="small text-secondary text-truncate">
          {contracting.placeOfDeliveryIdentification}
        </div>
      );
    } else {
      return <div className="small text-muted">-</div>;
    }
  };

  const renderButton = () => {
    if (forAdminUse) {
      return (
        <RoundButton
          asLink
          to={`/admin/contractings/${contracting.contractingId}`}
        />
      );
    } else {
      if (contracting.contractingState === "ColectingData") {
        return (
          <RoundButton
            icon="edit"
            asLink
            to={{
              pathname: "/contracting/add",
              search: getQueryParams(
                "contractingId",
                contracting.contractingId!
              ).toString(),
            }}
          />
        );
      } else {
        return (
          <RoundButton
            asLink
            to={`/contracting/${contracting.contractingId}`}
          />
        );
      }
    }
  };

  // render
  return (
    <div className="row no-gutters align-items-center">
      <div className="col">
        <div className="row no-gutters align-items-center">
          <div className="col-7 col-sm pr-3">
            {renderCustomerType()}
            {renderPlaceOfDelivery()}
          </div>
          {renderCustomerCol()}
          <div className={getEffectiveDateClass()}>{renderEffectiveDate()}</div>
          <div className="d-none d-lg-block col">
            <DateFormatter date={contracting.createdDate!} />
          </div>
          <div className="col-5 col-sm">
            <div className="d-inline-flex flex-column mw-100">
              <ContractingStateLabel
                state={contracting.contractingState!}
                textType={forAdminUse ? "admin" : "customer"}
              />
              <div className="d-lg-none small text-muted mt-1">
                <DateFormatter date={contracting.createdDate!} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={`col-auto pl-3`}>
        <div className="d-flex flex-column flex-lg-row align-items-end align-items-lg-center justify-content-lg-end">
          <div>{renderButton()}</div>
        </div>
      </div>
    </div>
  );
};

export default ContractingSimplified;
